import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import StarIcon from '@mui/icons-material/Star';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import Countdown from 'react-countdown';

import ExtensionSubMenu from '@/components/nav/ExtensionSubMenu';
import Popup7004 from '@/components/ui/7004Popup';
import BlogContentExtension from '@/components/ui/BlogContentExtension';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
import type { LayoutWrapperContents } from '@/types/LayoutWrapperContents';
import { getLastDateOfTheMonth } from '@/utils/utils';

const Form7004 = () => {
  const [referralUrl, SetReferralUrl] = useState('form7004');

  const layoutWrapperContents: LayoutWrapperContents = {
    Heading: `Need more time to file your business tax return?`,
    SubHeading: 'E-file Form 7004 & get a 6-month extension.',
    CtaText: 'E-file Form 7004 Now',
    CtaSubHeading: `Complete in 3 easy steps`,
    CtaURL: `${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form7004sticky`,
    isActive: true,
  };

  useEffect(() => {
    const isIRSPageReferral = document.referrer === 'https://www.irs.gov/';
    if (isIRSPageReferral) SetReferralUrl('form7004irs');
  }, []);

  const [lastDayTaxSeason, setLastDayTaxSeason] = useState(new Date());

  useEffect(() => {
    setLastDayTaxSeason(getLastDateOfTheMonth(new Date().getFullYear(), 4, 15));
    setInterval(() => {
      setLastDayTaxSeason(
        getLastDateOfTheMonth(new Date().getFullYear(), 4, 15)
      );
    }, 1000); // Updates every second (adjust if needed)
  }, []);

  // Random component
  const Completionist = () => <span />;

  // Renderer callback with condition
  const renderer = ({
    days,
    hours,
    minutes,
    seconds,
    completed,
  }: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) {
      // Render a completed state
      return <Completionist />;
    }
    // Render a countdown
    return (
      <Stack
        direction="row"
        justifyContent={{ xs: 'center', md: 'flex-start' }}
        alignItems="center"
        spacing={1}
        sx={{
          '& .timer-count': {
            border: '1px solid #848484',
            p: 1.5,
            textAlign: 'center',
            minWidth: { xs: 55, sm: 70, md: 70 },
            position: 'relative',
            borderRadius: 2,
            boxShadow: ' 0 0 20px rgba(0,0,0,.12)',
          },
          '& .timer-number': {
            fontSize: { xs: 18, sm: 24 },
            fontWeight: 900,
          },
          '& .colon': {
            fontSize: { xs: 18, sm: 24 },
            px: { xs: 0.2, sm: 0.5 },
            py: 0,
            mt: { xs: '-8px !important', sm: '-14px !important' },
          },
          '& .timer-label': {
            background: '#000',
            borderTop: '1px solid #848484',
            fontSize: { xs: 10, sm: 12 },
            fontWeight: 500,
            mt: 0,
            mx: -1.5,
            mb: -1.5,
            py: 0.2,
            px: { xs: 0.5, sm: 1, lg: 0.5 },
            borderRadius: '0px 0px 8px 8px',
          },
        }}
      >
        <Box className="timer-count">
          <Box className="timer-number">
            {days?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Days</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {hours?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Hours</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {minutes?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Minutes</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {seconds?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Seconds</Box>
        </Box>
      </Stack>
    );
  };

  return (
    <AppLayout
      LayoutWrapperContents={layoutWrapperContents}
      meta={
        <Meta
          title="E-File Form 7004 Online | Business Tax Extension"
          description="Need more time for your business taxes? E-File Form 7004 for an automatic 6-month extension. File with TaxZerone for just $17.99 per return."
          keywords="7004 form, form 7004 instructions, form 7004 extension, file form 7004 online, e file form 7004 tax extension, IRS form 7004, form 7004 online, e file extension 7004, IRS approved online tax filing, e-file Form 7004"
          canonical="tax-extension/efile-irs-form-7004-business-tax-extension-online/"
          jsonLdPricing="17.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="23852"
          ogShareImg="assets/images/extension-share-img.png"
        />
      }
    >
      <ExtensionSubMenu />
      <Popup7004 />
      <Container
        sx={{
          position: 'relative',
          mt: 2,
          mb: 0,
        }}
        maxWidth="lg"
      >
        <Box
          sx={{
            backgroundColor: '#2f3e46',
            p: 1.5,
            px: 2.5,
            color: '#fff',
          }}
          className="ribbon"
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Typography
              component="div"
              variant="body1"
              sx={{
                textAlign: { xs: 'center', sm: 'left' },
                fontSize: { xs: 15, sm: 17, md: 20 },
                color: '#e1e1e1',
                lineHeight: 1.3,
              }}
            >
              <strong>📢 Attention Business Owners!</strong> E-file{' '}
              <span style={{ fontWeight: 600, color: '#eae2b7' }}>
                Form 7004
              </span>{' '}
              today and get up to{' '}
              <span style={{ fontWeight: 600, color: '#eae2b7' }}>
                6 extra months
              </span>{' '}
              to file your return.
            </Typography>

            <Box sx={{}}>
              {/* <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  mb: { xs: 1, sm: 1 },
                  lineHeight: 1.3,
                }}
              >
                Time Left Until the Deadline:{' '}
                <span style={{ fontWeight: 600, color: '#fff' }}>
                  April 15, 2026
                </span>
              </Typography> */}
              <Countdown date={lastDayTaxSeason} renderer={renderer} />
            </Box>

            <Box>
              <Button
                variant="contained"
                size="small"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form7004`}
                sx={{
                  backgroundColor: '#fff',
                  '&:hover, &:focus': {
                    backgroundColor: '#213e99',
                    color: '#fff',
                  },
                  px: 2,
                  color: '#333',
                  mt: { xs: 0, md: 0 },
                  display: { xs: 'inline-block', sm: 'inline-block' },
                  mx: { xs: 'auto', sm: 1 },
                  borderRadius: 3,
                  boxShadow: 'none',
                  fontSize: { xs: 14, sm: 16, md: 18 },
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Request a 6-Month Extension
              </Button>
            </Box>
          </Stack>
        </Box>
      </Container>
      <Container maxWidth="xl" sx={{ mt: { xs: 0, lg: 0 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="div"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                E-file{' '}
                <span className="highlight-container">
                  <span className="highlight">Form 7004</span>
                </span>{' '}
                for Tax Year 2025
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1,
                }}
                component="div"
              >
                <DoubleArrowIcon
                  sx={{ mr: 1, alignSelf: 'flex-start', mt: 0.4 }}
                />{' '}
                <Typography
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                  }}
                >
                  File Form 7004 online in just{' '}
                  <span style={{ fontWeight: 600 }}>3 simple steps</span> and
                  get an extension of up to 6 months.
                </Typography>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1,
                }}
                component="div"
              >
                <DoubleArrowIcon
                  sx={{ mr: 1, alignSelf: 'flex-start', mt: 0.4 }}
                />{' '}
                <Typography
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                  }}
                >
                  E-File Before the Deadline to Avoid Penalties
                </Typography>
              </Typography>

              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Box sx={{ textAlign: 'center', mr: { xs: 0, md: 4 } }}>
                  <Button
                    variant="contained"
                    className="btn-primary"
                    sx={{
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
                  >
                    E-File Form 7004 Now
                    {/* <Box
                      component={'span'}
                      sx={{ ml: 1, color: '#eaff00', fontWeight: 700 }}
                    >
                      $11.99
                    </Box> */}
                  </Button>
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontSize: { xs: 11, sm: 14 } }}
                  >
                    Takes 3 steps and less than 5 minutes
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent={{
                    xs: 'center',
                    md: 'flex-start',
                  }}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      mb: { xs: '0 !important', md: '22px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/irs-authorized.webp" />
                      <source srcSet="/assets/images/irs-authorized.png" />
                      <Box
                        component="img"
                        src="/assets/images/irs-authorized.png"
                        loading="lazy"
                        alt="IRS-authorized e-file provider"
                        width={70}
                        height={69}
                        sx={{
                          width: { xs: 50, md: 70 },
                          height: { xs: 49, md: 69 },
                        }}
                      />
                    </picture>
                  </Box>
                  <Box
                    sx={{
                      mb: { xs: '0px !important', md: '22px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/tzo-soc2.webp" />
                      <source srcSet="/assets/images/tzo-soc2.png" />
                      <Box
                        component="img"
                        src="/assets/images/tzo-soc2.png"
                        loading="lazy"
                        alt="TZO SOC2 Compliant"
                        width={90}
                        height={89}
                        sx={{
                          width: { xs: 70, md: 90 },
                          height: { xs: 69, md: 89 },
                        }}
                      />
                    </picture>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
          <Hidden smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
              }}
              className="shining"
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/7004-ticket-img.webp"
                  className="img-responsive-banner"
                />
                <source
                  srcSet="/assets/images/7004-ticket-img.png"
                  className="img-responsive-banner"
                />
                <img
                  src="/assets/images/7004-ticket-img.png"
                  alt="E-file IRS Form 7004 before the due date and avoid penalties"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          {/* <Hidden mdUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
                position: 'relative',
                overflow: 'hidden',
              }}
              className="shining"
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/7004-ticket-img.webp"
                  className="img-responsive-banner"
                />
                <source
                  srcSet="/assets/images/7004-ticket-img.png"
                  className="img-responsive-banner"
                />
                <img
                  src="/assets/images/7004-ticket-img.png"
                  alt="E-file IRS Form 7004 before the due date and avoid penalties"
                  className="img-responsive-banner shining"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden> */}
        </Stack>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container
          maxWidth="lg"
          sx={{
            my: { xs: 0, md: 3, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 3 },
            borderRadius: { xs: 0, md: 4 },
            mx: { xs: 0, md: 3, lg: 'auto' },
          }}
          className="gradient-bg"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 3 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 26, sm: 22, xs: 20 },
                  fontWeight: 500,
                  mb: 0,
                  textAlign: 'left',
                  lineHeight: 1.5,
                  '& strong': {
                    color: '#ffe400',
                    fontSize: { xs: 30, sm: 36, md: 48, lg: 54 },
                  },
                }}
              >
                File Form 7004 online for as low as just <strong>$17.99</strong>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#f3f3f3',
                  mb: 0,
                  textAlign: 'left',
                }}
              >
                Enjoy the simplest and most affordable way to e-file your form
                7004. TaxZerone offers the best price in the industry. Save
                money and time with our user-friendly platform and transparent
                pricing options.
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100', sm: 295, md: 300 },
                textAlign: 'center',
              }}
            >
              <Button
                variant="contained"
                size="large"
                href="/pricing/#extension"
                sx={{
                  backgroundColor: '#fff',
                  '&:hover, &:focus': {
                    backgroundColor: '#213e99',
                    color: '#fff',
                  },
                  px: 2,
                  color: '#333',
                  mt: { xs: 0, md: 0 },
                  display: { xs: 'inline-block', sm: 'inline-block' },
                  mx: { xs: 'auto', sm: 1 },
                  borderRadius: 3,
                  boxShadow: 'none',
                  fontSize: 18,
                  textTransform: 'none',
                }}
              >
                Explore our Pricing Options
              </Button>
              <Typography
                variant="body2"
                sx={{ mt: 1, fontSize: { xs: 11, sm: 14 } }}
              >
                Start Saving Today!
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ my: { xs: 2, sm: 6, md: 6 } }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                3 Simple Steps to File IRS Form 7004 &<br /> Secure a 6-Month
                Tax Extension
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Follow these steps below to e-file irs tax extension form 7004
                online.
              </Typography>
            </Grid>
          </Grid>
        </Container>
        <Container maxWidth="xl" sx={{ mb: 5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                mt: { xs: -3, md: -5, lg: -8 },
              }}
            >
              <picture>
                <source
                  srcSet="/assets/images/7004-steps-img.webp"
                  className="img-responsive"
                  width={915}
                  height={790}
                />
                <source
                  srcSet="/assets/images/7004-steps-img.png"
                  className="img-responsive"
                  width={915}
                  height={790}
                />
                <img
                  src="/assets/images/7004-steps-img.png"
                  alt="How to E-File Form 7004 in 3 simple steps with TaxZerone?"
                  className="img-responsive"
                  loading="lazy"
                  width={915}
                  height={790}
                />
              </picture>
            </Box>

            <Box
              sx={{
                width: '100%',
              }}
            >
              <Box
                component="ol"
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                  p: 2,
                  pl: 0,
                  ml: { xs: 0, sm: 0, md: -2 },
                }}
              >
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        my: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source
                          srcSet="/assets/images/step-1-icon.webp"
                          width={39}
                          height={39}
                        />
                        <source
                          srcSet="/assets/images/step-1-icon.png"
                          width={39}
                          height={39}
                        />
                        <img
                          src="/assets/images/step-1-icon.png"
                          alt="Enter business information"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Enter business information
                    </Typography>
                    <Typography variant="body1">
                      Provide your business details (such as name, EIN, and
                      address), and choose the applicable tax return form (e.g.,
                      1120, 1065, 1041) for which you are requesting an
                      extension.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        m: 0,
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-2-icon.webp" />
                        <source srcSet="/assets/images/step-2-icon.png" />
                        <img
                          src="/assets/images/step-2-icon.png"
                          alt="Enter Tax Information"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Enter Tax Information
                    </Typography>
                    <Typography variant="body1">
                      Provide the tentative total tax liability, total taxes
                      paid, and applicable credits. TaxZerone will automatically
                      calculate the balance due.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-3-icon.webp" />
                        <source srcSet="/assets/images/step-3-icon.png" />
                        <img
                          src="/assets/images/step-3-icon.png"
                          alt="Transmit the return to the IRS"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Transmit the return to the IRS
                    </Typography>
                    <Typography variant="body1">
                      Securely review & transmit your extension request through
                      TaxZerone and receive an email notification from the IRS
                      in minutes.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 21, md: 20, sm: 18, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  textAlign: { xs: 'center', md: 'left' },
                  lineHeight: 1.5,
                }}
              >
                These steps look really simple, right? E-file Form 7004 now with
                TaxZerone.
              </Typography>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 4,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
                >
                  Get an Extension Today
                </Button>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, mb: { xs: 2, sm: 2, md: 5 } }}
                >
                  You are just 3 steps away from getting an extension.
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Box
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: { lg: 6, md: 6, sm: 6, xs: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column', md: 'row' } }}
          >
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box
                sx={{
                  mb: { xs: 3, md: 0 },
                  mt: { xs: -3, md: 0 },
                  mr: { md: 3 },
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  What is IRS 7004 Tax Form?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Understand how this simple form 7004 grants you a valuable
                  6-month extension for specific business tax returns.
                </Typography>
                <Box
                  component="ol"
                  sx={{
                    [`& .${timelineItemClasses.root}:before`]: {
                      flex: 0,
                      padding: 0,
                    },
                    p: 2,
                    pl: 0,
                    ml: { xs: 0, sm: 0, md: -2 },
                  }}
                >
                  <TimelineItem>
                    <TimelineSeparator>
                      <StarIcon sx={{ mt: 0.7 }} />
                    </TimelineSeparator>
                    <TimelineContent sx={{ pl: 0.5, mb: 1 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                          fontWeight: 600,
                          mt: 0,
                          mb: 0,
                          color: '#36324a',
                        }}
                      >
                        What it is:
                      </Typography>
                      <Typography variant="body1">
                        An official IRS form used to request an extension for
                        filing various business tax forms.
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                  <TimelineItem>
                    <TimelineSeparator>
                      <StarIcon sx={{ mt: 0.7 }} />
                    </TimelineSeparator>
                    <TimelineContent sx={{ pl: 0.5, mb: 1 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                          fontWeight: 600,
                          mt: 0,
                          mb: 0,
                          color: '#36324a',
                        }}
                      >
                        What it offers:
                      </Typography>
                      <Typography variant="body1">
                        A 6-month extension beyond the original due date,
                        granting you valuable breathing room to gather records
                        and complete your return accurately.
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                  <TimelineItem>
                    <TimelineSeparator>
                      <StarIcon sx={{ mt: 0.7 }} />
                    </TimelineSeparator>
                    <TimelineContent sx={{ pl: 0.5, mb: 1 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                          fontWeight: 600,
                          mt: 0,
                          mb: 0,
                          color: '#36324a',
                        }}
                      >
                        What forms does it apply to:
                      </Typography>
                      <Typography variant="body1">
                        A range of business tax forms, including income tax
                        returns (Forms 1120, 1120S, etc.), partnership returns
                        (Form 1065), estate and trust returns (Form 1041), and
                        certain information returns.
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>

                  <TimelineItem>
                    <TimelineSeparator>
                      <StarIcon sx={{ mt: 0.7 }} />
                    </TimelineSeparator>
                    <TimelineContent sx={{ pl: 0.5, mb: 1 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                          fontWeight: 600,
                          mt: 0,
                          mb: 0,
                          color: '#36324a',
                        }}
                      >
                        How to file:
                      </Typography>
                      <Typography variant="body1">
                        You can efile Form 7004 (recommended) or by mail.
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                  <TimelineItem>
                    <TimelineSeparator>
                      <StarIcon sx={{ mt: 0.7 }} />
                    </TimelineSeparator>
                    <TimelineContent sx={{ pl: 0.5 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                          fontWeight: 600,
                          mt: 0,
                          mb: 0,
                          color: '#36324a',
                        }}
                      >
                        When to file:
                      </Typography>
                      <Typography variant="body1">
                        Form 7004 must be filed by the original due date of the
                        return you&apos;re seeking an extension for.
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                </Box>

                <Box
                  sx={{
                    width: 'auto',
                    textAlign: 'left',
                    alignItems: 'center',
                    display: 'inline-block',
                    mt: { xs: 1, sm: 1 },
                  }}
                >
                  <Button
                    variant="contained"
                    className="btn-primary medium"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
                    sx={{
                      mb: 2,
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                  >
                    E-file Your Form 7004 Now!
                  </Button>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> With TaxZerone, you can easily{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1120-S
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1065-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 1065
                  </Link>{' '}
                  for the 2025 tax year, helping S Corporations and Partnerships
                  stay accurate and on time.
                </Alert>
              </Box>
            </Grid>
            <Grid size={{ md: 6, xs: 12 }} textAlign="right">
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  '& img': {
                    borderRadius: 4,
                    boxShadow: '0 0 20px rgba(0,0,0,.15)',
                  },
                }}
              >
                <picture>
                  <source srcSet="/assets/images/7004-img.webp" />
                  <source srcSet="/assets/images/7004-img.png" />
                  <img
                    src="/assets/images/7004-img.png"
                    alt="E-file IRS Form 7004 to get up to 6-month of extension "
                    className="img-responsive-banner"
                    width={645}
                    height={590}
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          py: { xs: 3, sm: 5, md: 6, lg: 8 },
          mx: '0 !important',
          width: '100% !important',
          backgroundSize: { xs: '25% auto', sm: '15% auto', md: '10% auto' },
          // position: { xs: 'static', md: 'sticky' },
          // top: 64,
          // zIndex: 99,
        }}
        spacing={2}
        m={0}
        //  className="wave-bg"
      >
        <Container maxWidth="xl" sx={{ mb: 0 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 24, md: 18, sm: 16, xs: 16 },
              fontWeight: 600,
              mb: { xs: 3, md: 1 },
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            <Link href="/features/click2file/" sx={{ color: '#0202ff' }}>
              Click2File:
            </Link>{' '}
            Create Current Year Return from Prior Year Data
          </Typography>
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid size={{ md: 6, xs: 12 }} textAlign="right">
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <picture>
                  <source srcSet="/assets/images/7004-click2file-img.webp" />
                  <source srcSet="/assets/images/7004-click2file-img.png" />
                  <img
                    src="/assets/images/7004-click2file-img.png"
                    alt="Click2File: Create Current Year Return from Prior Year Data"
                    className="img-responsive-banner"
                    width={645}
                    height={590}
                  />
                </picture>
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box
                sx={{
                  mb: { xs: 3, md: 0 },
                  mt: { xs: -3, md: 0 },
                  ml: { md: 3 },
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent={{ xs: 'center', md: 'flex-start' }}
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 3 }}
                >
                  <Box
                    component="img"
                    src="/assets/images/click2file-icon.svg"
                    width={120}
                    height={120}
                    alt="Click2File - Tax Season Simplified"
                  />
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                      fontWeight: 600,
                      mb: 1,
                      textAlign: { xs: 'center', md: 'left' },
                      // lineHeight: 1.5,
                      '& .sub-title': {
                        fontSize: { md: 26, sm: 22, xs: 18 },
                      },
                    }}
                  >
                    Tax Season Simplified:
                    <br />
                    <Box component="div" className="sub-title">
                      Pre-Populate Your Current Return with Click2File!
                    </Box>
                  </Typography>
                </Stack>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    color: '#333333',
                    mb: 3,
                  }}
                >
                  Click2File takes the hassle out of tax preparation by
                  automatically transferring relevant data from your previous
                  return to your current one.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#333333',
                    fontSize: { lg: 24, md: 24, sm: 24, xs: 24 },
                    mb: 1,
                  }}
                >
                  How it Works
                </Typography>
                <Box
                  component="ol"
                  sx={{
                    [`& .${timelineItemClasses.root}:before`]: {
                      flex: 0,
                      padding: 0,
                    },
                    p: 2,
                    pl: 0,
                    ml: { xs: 0, sm: 0, md: -2 },
                  }}
                >
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot
                        sx={{
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                          my: -1,
                          mb: -1.7,
                        }}
                      >
                        <picture>
                          <source
                            srcSet="/assets/images/step-1-icon.webp"
                            width={39}
                            height={39}
                          />
                          <source
                            srcSet="/assets/images/step-1-icon.png"
                            width={39}
                            height={39}
                          />
                          <img
                            src="/assets/images/step-1-icon.png"
                            alt="Login and Select"
                            loading="lazy"
                            width={39}
                            height={39}
                          />
                        </picture>
                      </TimelineDot>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent sx={{ mb: 2 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                          fontWeight: 600,
                          mt: 0,
                          mb: 1,
                          color: '#36324a',
                        }}
                      >
                        Login and Select:
                      </Typography>
                      <Typography variant="body1">
                        Simply access your TaxZerone account and choose
                        Click2File Form 7004.
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot
                        sx={{
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                          m: 0,
                          mt: -1,
                          mb: -1.7,
                        }}
                      >
                        <picture>
                          <source srcSet="/assets/images/step-2-icon.webp" />
                          <source srcSet="/assets/images/step-2-icon.png" />
                          <img
                            src="/assets/images/step-2-icon.png"
                            alt="Review and Customize"
                            loading="lazy"
                            width={39}
                            height={39}
                          />
                        </picture>
                      </TimelineDot>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent sx={{ mb: 2 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                          fontWeight: 600,
                          mt: 0,
                          mb: 1,
                          color: '#36324a',
                        }}
                      >
                        Review and Customize:
                      </Typography>
                      <Typography variant="body1">
                        Click2File automatically populates your current return
                        with relevant details. Review and adjust anything to
                        reflect your current financial situation.
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot
                        sx={{
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                          mt: -1,
                          mb: -1.7,
                        }}
                      >
                        <picture>
                          <source srcSet="/assets/images/step-3-icon.webp" />
                          <source srcSet="/assets/images/step-3-icon.png" />
                          <img
                            src="/assets/images/step-3-icon.png"
                            alt="File with Ease"
                            loading="lazy"
                            width={39}
                            height={39}
                          />
                        </picture>
                      </TimelineDot>
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                          fontWeight: 600,
                          mt: 0,
                          mb: 1,
                          color: '#36324a',
                        }}
                      >
                        File with Ease:
                      </Typography>
                      <Typography variant="body1">
                        Once everything is perfect, submit your return
                        electronically. Click2File makes filing a breeze!
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                </Box>

                <Box
                  sx={{
                    width: 'auto',
                    textAlign: 'center',
                    alignItems: 'center',
                    display: 'inline-block',
                    mt: 0,
                  }}
                >
                  <Button
                    variant="contained"
                    className="btn-primary medium"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
                    sx={{
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                  >
                    Start Filing Now
                  </Button>
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, mb: 0, textAlign: 'center' }}
                  >
                    Experience the convenience of Click2File
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                How TaxZerone simplifies filing Form 7004 online?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Simplify Your Form 7004 Filing with TaxZerone&apos;s Easy and
                Powerful Features
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} m={2} mt={4} pb={{ xs: 0, sm: 4, md: 7 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/form-based-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/form-based-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/form-based-icon.png"
                      alt="Form-Based Filing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Form-based filing
                </Typography>
                <Typography variant="body1">
                  You fill out the form just as you complete a physical form,
                  but instead of a pen you type out the required information.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/guided-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/guided-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/guided-icon.png"
                      alt="Guided filing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Guided filing
                </Typography>
                <Typography variant="body1">
                  Our IRS{' '}
                  <Link
                    href="/resources/tax-extension/form-7004-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 7004 instructions
                  </Link>{' '}
                  will guide you along the way to complete your extension filing
                  without any hassles.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/smart-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/smart-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/smart-icon.png"
                      alt="Smart validations"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Smart validations
                </Typography>
                <Typography variant="body1">
                  We&apos;ll validate your return based on IRS rules and alert
                  you if there are any errors, thereby reducing the chances of
                  rejection.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/secure-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/secure-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/secure-icon.png"
                      alt="Quick and secure e-filing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Quick and secure e-filing
                </Typography>
                <Typography variant="body1">
                  File Form 7004 online with TaxZerone in just 5 minutes and
                  transmit your return to the IRS securely. It&apos;s that
                  simple.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/instant-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/instant-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/instant-icon.png"
                      alt="Instant return updates"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Instant return updates
                </Typography>
                <Typography variant="body1">
                  Receive notifications on your return status as soon as the IRS
                  processes it. Also, check the status from your TaxZerone
                  account.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/free-efile-retransmission-icon.png"
                      alt="Free retransmission of rejected returns"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Free retransmission of rejected returns
                </Typography>
                <Typography variant="body1">
                  Rest assured, if the IRS happens to reject your Form 7004 for
                  any reason, you have the flexibility to make corrections and
                  resubmit it to the IRS, all at no additional cost. Your peace
                  of mind is our priority.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/irs-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/irs-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/irs-icon.png"
                      alt="IRS-authorized"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  IRS-authorized
                </Typography>
                <Typography variant="body1">
                  File your Business Tax Extension Form 7004 with an
                  IRS-authorized e-file service provider and rest assured that
                  your return will be accepted by the IRS, leaving you
                  worry-free.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/time-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/time-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/time-icon.png"
                      alt="Timely Reminders"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Timely Reminders
                </Typography>
                <Typography variant="body1">
                  Stay on top of your tax obligations with our Timely Reminders
                  feature. Receive proactive notifications to ensure you never
                  miss a deadline when filing tax forms.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/support-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/support-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/support-icon.png"
                      alt="Dedicated Customer Support"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Dedicated Customer Support
                </Typography>
                <Typography variant="body1">
                  Our team of Form 7004 experts is always here to answer your
                  questions and guide you through the filing process.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Box
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          py: { lg: 6, md: 6, sm: 5, xs: 4 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            //  color="white"
            sx={{
              fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
              fontWeight: 600,
              mb: 2,
            }}
          >
            Form 7004 Due Dates
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
              fontWeight: 600,
              //  color: '#f3f3f3',
              mb: 3,
            }}
          >
            The Golden Rule: File Form 7004 on or before the original due date
            of the return you&apos;re seeking an extension for.
          </Typography>
          <Box>
            <picture>
              <source srcSet="/assets/images/duedates-img.webp" />
              <source srcSet="/assets/images/duedates-img.png" />
              <img
                src="/assets/images/duedates-img.png"
                loading="lazy"
                alt="Form 7004 Due Dates"
                className="img-responsive-banner"
                width={1000}
                height={600}
              />
            </picture>
          </Box>
          <Button
            variant="contained"
            sx={{
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            className="btn-primary medium"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
          >
            File Your Form 7004 Now!
          </Button>
          <Typography
            variant="body2"
            sx={{ mt: 1, mb: { xs: 2, sm: 0 } }}
            //  color="#dfeae1"
          >
            Beat the Deadline
          </Typography>
        </Container>
      </Box>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
          <Grid
            container
            spacing={2}
            m={2}
            mt={{ xs: 5, sm: 5, md: 6 }}
            mb={{ xs: 5, sm: 5, md: 8 }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2 }}
                justifyContent={{ xs: 'center', md: 'center' }}
              >
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-1"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-2"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-3"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-4"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-5"
                />
              </Stack>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: { xs: 1, sm: 3 },
                }}
              >
                Real Businesses, Real Feedback:
                <br />
                What People Say About TaxZerone&apos;s Easy e-Filing Solution
              </Typography>
              {/* <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Read the satisfied truckers&apos; testimonials and see why
                TaxZerone is the trusted choice for stress-free e-filing.
              </Typography> */}
              <Grid
                container
                spacing={2}
                m={0}
                justifyContent="center"
                direction="row"
                className="MuiGrid-root-width"
              >
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#fff',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        TaxZerone made filing Form 7004 a breeze! As a small
                        business owner, time is precious, and TaxZerone&apos;s
                        user-friendly interface and step-by-step guidance saved
                        me hours. I got my extension quickly, and their support
                        team was responsive. Highly recommended! <br />
                        <span style={{ fontWeight: 600 }}>
                          John - <small>Small Business Owner</small>
                        </span>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#fff',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        TaxZerone simplified the complex world of tax filing for
                        startups. Filing Form 7004 was straightforward, and I
                        appreciated the informative guides that clarified the
                        process. The peace of mind it provided during tax season
                        was invaluable. <br />
                        <span style={{ fontWeight: 600 }}>
                          Emily - <small>Startup Founder</small>
                        </span>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#fff',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        TaxZerone has become my go-to platform for Form 7004
                        extensions. The process is streamlined, and I appreciate
                        the accuracy checks that ensure my submissions are
                        error-free. <br />
                        <span style={{ fontWeight: 600 }}>
                          Sarah - <small>Tax Professional</small>
                        </span>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#fff',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        Was referred to your website from IRS site. Super easy
                        to use and felt that the price was extremely fair for
                        the 7004 filing. <br />
                        <span style={{ fontWeight: 600 }}>
                          Maria - <small>Business Owner</small>
                        </span>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#fff',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        I was able to very quickly set up my account. Price was
                        very reasonable.
                        <br />
                        <span style={{ fontWeight: 600 }}>
                          David - <small>Tax Professional</small>
                        </span>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          py: 0,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
          >
            <Grid size={{ md: 6, xs: 12 }} textAlign="right">
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <picture>
                  <source
                    srcSet="/assets/images/7004-mobile-app-img.webp"
                    className="img-responsive"
                  />
                  <source
                    srcSet="/assets/images/7004-mobile-app-img.png"
                    className="img-responsive"
                  />
                  <img
                    src="/assets/images/7004-mobile-app-img.png"
                    alt="File your Business Tax Extension Anytime , Anywhere with Our Mobile App!"
                    className="img-responsive"
                    loading="lazy"
                    width={657}
                    height={770}
                  />
                </picture>
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 3, md: 0 }, mt: { xs: -3, md: 0 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  File your Business Tax Extension Anytime, Anywhere with Our
                  Mobile App!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 3,
                  }}
                >
                  A mobile app from TaxZerone for e-filing Form 7004, lets you
                  manage tax extensions on the go. Submit quickly, get secure
                  access, and receive deadline reminders
                </Typography>

                <Typography
                  variant="body1"
                  color="secondary"
                  sx={{ fontWeight: 600 }}
                >
                  Download Our App Now and Get Ready to File Your Tax Extension
                </Typography>
                <Box
                  sx={{
                    my: 1,
                    mb: 0,
                    '& img': {
                      width: { xs: 120, sm: 180 },
                      height: { xs: 37, sm: 55 },
                    },
                  }}
                >
                  <Link
                    href="https://play.google.com/store/apps/details?id=com.taxzerone.extension7004"
                    target="_blank"
                    sx={{
                      mr: { xs: 1, sm: 1 },
                      my: { xs: 0, sm: 0 },
                      display: 'inline-block',
                    }}
                  >
                    <Tooltip title="Available on the Google Play" arrow>
                      <picture>
                        <source
                          srcSet="/assets/images/play-store-img.webp"
                          width={180}
                          height={55}
                        />
                        <source
                          srcSet="/assets/images/play-store-img.png"
                          width={180}
                          height={55}
                        />
                        <img
                          src="/assets/images/play-store-img.png"
                          alt="Available on the Google Play"
                          width={180}
                          height={55}
                        />
                      </picture>
                    </Tooltip>
                  </Link>
                  <Link
                    href="https://apps.apple.com/us/app/e-file-irs-extension-form-7004/id6478196240"
                    target="_blank"
                    sx={{
                      ml: { xs: 1, sm: 1 },
                      my: { xs: 0, sm: 0 },
                      display: 'inline-block',
                    }}
                  >
                    <Tooltip title="Download on the App Store" arrow>
                      <picture>
                        <source
                          srcSet="/assets/images/app-store-img.webp"
                          width={180}
                          height={55}
                        />
                        <source
                          srcSet="/assets/images/app-store-img.png"
                          width={180}
                          height={55}
                        />
                        <img
                          src="/assets/images/app-store-img.png"
                          alt="Download on the App Store"
                          width={180}
                          height={55}
                        />
                      </picture>
                    </Tooltip>
                  </Link>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ mt: 0, mb: { xs: 2, md: 2 } }}
                  //  color="#dfeae1"
                >
                  Don&apos;t wait until the last minute - file your extension
                  effortlessly with TaxZerone!
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 }, mb: 3 }}>
          <Grid
            container
            spacing={2}
            m={2}
            mt={{ xs: 5, sm: 5, md: 6 }}
            mb={{ xs: 5, sm: 5, md: 8 }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                //  color="white"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Learn How to File IRS Extension Form 7004 Through Videos
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} textAlign="center">
              {' '}
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'center', md: 'flex-end' },
                  alignItems: 'center',
                  height: '100%',
                  width: '100%',
                  mt: 3,
                }}
              >
                <Box
                  component="iframe"
                  title="Secret Hack to E-file Form 7004 in Minutes - It's Easier Than You Think!"
                  src="https://www.youtube.com/embed/cFWejqJ80VQ?&rel=0"
                  sx={{
                    width: { xs: '100%', sm: 500 },
                    height: { xs: '100%', sm: 281 },
                    border: '10px solid #333 !important',
                    borderRadius: 5,
                  }}
                  allowFullScreen={true}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} textAlign="center">
              {' '}
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'center', md: 'flex-end' },
                  alignItems: 'center',
                  height: '100%',
                  width: '100%',
                  mt: 3,
                }}
              >
                <Box
                  component="iframe"
                  title="TaxZerone's Click2File: Create Current Year Return from Prior Year Data"
                  src="https://www.youtube.com/embed/rEsXdf5Vy7M?&rel=0"
                  sx={{
                    width: { xs: '100%', sm: 500 },
                    height: { xs: '100%', sm: 281 },
                    border: '10px solid #333 !important',
                    borderRadius: 5,
                  }}
                  allowFullScreen={true}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          mb: 0,
        }}
        className="curve-bg"
      >
        <Container sx={{ maxWidth: '1000px !important' }}>
          <Grid
            container
            spacing={2}
            m={2}
            mt={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                color="white"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                Get the Forms You Need: Form 7004 PDFs
              </Typography>

              <Typography
                variant="h3"
                sx={{
                  fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
                  fontWeight: 400,
                  color: '#f3f3f3',
                  mb: 3,
                }}
              >
                Download the official forms and instructions directly from the
                IRS website
              </Typography>
              <Box
                component="ul"
                sx={{
                  color: '#f3f3f3',
                  textAlign: 'left',
                  listStyleType: 'none',
                }}
              >
                <li style={{ marginBottom: 12 }}>
                  <CheckCircleOutlineIcon sx={{ mr: 1, mb: -0.7 }} />
                  Form 7004 (Application for Automatic Extension of Time To File
                  Certain Business Income Tax, Information, and Other Returns){' '}
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/f7004.pdf"
                    target="_blank"
                    sx={{ color: '#000' }}
                  >
                    Download Form 7004 PDF
                  </Link>
                </li>
                <li>
                  <CheckCircleOutlineIcon sx={{ mr: 1, mb: -0.7 }} />
                  Instructions for Form 7004{' '}
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/i7004.pdf"
                    target="_blank"
                    sx={{ color: '#000' }}
                  >
                    Download Instructions PDF for Form 7004
                  </Link>
                </li>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
                  fontWeight: 600,
                  color: '#fff',
                  mb: 1,
                  mt: 5,
                }}
              >
                Ready to experience the ease of e-filing Form 7004?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#f3f3f3',
                  mb: 3,
                }}
              >
                E-filing is more than just a trend, it&apos;s a smarter way to
                file. So, switch to e-file and embrace the future!
              </Typography>
              <Box component="div" sx={{ margin: 'auto' }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: { xs: 0, sm: 0 },
                    mb: { xs: 0, md: 0 },
                    color: '#444',
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &active': {
                      backgroundColor: '#213e99 !important',
                      color: '#fff',
                      transform: 'scale(1.1)',
                    },
                  }}
                  className="btn-secondary-white"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
                >
                  E-File and Get an Extension Today
                </Button>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, mb: { xs: 0, md: 0 } }}
                  color="#dfeae1"
                >
                  Take the first step to discover the simplicity of e-filing.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Container maxWidth="lg" sx={{ mt: 3, mb: { xs: 3, sm: 5 } }}>
        <Grid container spacing={2} m={0} pb={0} className="MuiGrid-root-width">
          <Grid size={12} textAlign="center">
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: { xs: 0, sm: 3 },
                textAlign: 'center',
                lineHeight: 1.3,
                mt: 0,
              }}
            >
              Recent Blogs
            </Typography>
          </Grid>

          <BlogContentExtension />
        </Grid>
      </Container>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              FAQs on Form 7004
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is Form 7004?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    The IRS Form 7004 is an extension form filed by businesses
                    to get an automatic extension of up to 6 months to file
                    certain business income tax. The IRS doesn&apos;t require
                    any signature or a valid reason while filing an extension.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. When is the deadline to file Form 7004?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography component="div">
                      The filing{' '}
                      <Link
                        href="/resources/tax-extension/form-7004-due-dates/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        deadline for Form 7004
                      </Link>{' '}
                      depends on the original due date of your business tax
                      return. Here are the key deadlines you need to know:
                    </Typography>
                    <Box component="ul" sx={{ mb: 1.5, '& li': { mb: 1 } }}>
                      <li>
                        <span style={{ fontWeight: 600 }}>March 15</span> – For
                        S corporations (
                        <Link
                          href="/resources/business-tax/form-1120-s-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 1120-S
                        </Link>
                        ) & partnerships (
                        <Link
                          href="/resources/business-tax/form-1065-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 1065
                        </Link>
                        )
                      </li>
                      <li>
                        {' '}
                        <span style={{ fontWeight: 600 }}>April 15</span> – For
                        C corporations (
                        <Link
                          href="/resources/tax-extension/form-7004-for-form-1120/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 1120
                        </Link>
                        ) and certain other business returns
                      </li>
                    </Box>
                    <Typography variant="body1">
                      Filing by these dates grants an automatic extension,
                      typically six months, to file your return. An extension
                      grants additional time to file your return but does not
                      extend the deadline for paying any tax due.
                    </Typography>
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{
                        mt: 1,
                        // ml: { sm: 5, xs: 1 },
                        // mr: { sm: 8, xs: 1 },
                        // mb: { sm: 2, xs: 1 },
                        textAlign: 'left',
                        borderLeft: '3px solid rgb(102, 60, 0)',
                      }}
                    >
                      💡 Easily calculate your filing deadline with our accurate{' '}
                      <Link
                        href="/tools/form-7004-due-date-calculator/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 7004 due date calculator
                      </Link>
                      .
                    </Alert>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Who can file Form 7004?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography>
                      Most business entities can file Form 7004 , except for
                      sole proprietors. Below businesses can use Form 7004 irs
                      to get an extension to file business income tax:
                    </Typography>
                    <List sx={{ pb: 0 }}>
                      <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemIcon
                          sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                        >
                          <DoubleArrowIcon />
                        </ListItemIcon>
                        <ListItemText>
                          C-corps <strong>(Form 1120)</strong>
                        </ListItemText>
                      </ListItem>
                      <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemIcon
                          sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                        >
                          <DoubleArrowIcon />
                        </ListItemIcon>
                        <ListItemText>
                          S-corps <strong>(Form 1120S)</strong>
                        </ListItemText>
                      </ListItem>
                      <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemIcon
                          sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                        >
                          <DoubleArrowIcon />
                        </ListItemIcon>
                        <ListItemText>
                          Partnerships <strong>(Form 1065)</strong>
                        </ListItemText>
                      </ListItem>
                      <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemIcon
                          sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                        >
                          <DoubleArrowIcon />
                        </ListItemIcon>
                        <ListItemText>
                          Multiple-member LLCs as corporations and partnerships
                        </ListItemText>
                      </ListItem>
                      <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemIcon
                          sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                        >
                          <DoubleArrowIcon />
                        </ListItemIcon>
                        <ListItemText>
                          Trust, Estates and Others <strong>(Form 1041)</strong>
                        </ListItemText>
                      </ListItem>
                    </List>
                  </Box>
                </Box>
              </Box>

              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. Can I file Form 7004 electronically?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography>
                      Yes, you can definitely file Form 7004 electronically! In
                      fact, the IRS encourages electronic filing for Form 7004
                      as it is faster, more accurate, and more convenient than
                      filing on paper.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                  itemProp="name"
                >
                  5. What is the difference between Form 7004 and 4868?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <TableContainer sx={{ border: '1px solid #828282' }}>
                      <Table
                        sx={{}}
                        aria-label="difference between form 7004 and 4868"
                      >
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#ccc' }}>
                            <TableCell>Form 7004</TableCell>
                            <TableCell>Form 4868</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow sx={{}}>
                            <TableCell>
                              Used by businesses, including partnerships,
                              multi-member LLCs, C corporations, and S
                              corporations, to request an automatic extension of
                              time to file certain business income tax,
                              information, and other returns.
                            </TableCell>
                            <TableCell>
                              Used by individuals (including sole proprietors
                              and single-member LLCs) and resident aliens to
                              request an automatic extension of time to file
                              their U.S. individual income tax return.
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                            <TableCell>
                              Examples of returns covered by Form 7004 include
                              Form 1120 (Corporate Income Tax Return), Form 1065
                              (Partnership Return of Income), and Form 1041
                              (U.S. Income Tax Return for Estates and Trusts).
                            </TableCell>
                            <TableCell>
                              Covers Form 1040 (U.S. Individual Income Tax
                              Return), Form 1040NR (U.S. Nonresident Alien
                              Income Tax Return), and Form 1040NR-EZ (U.S.
                              Nonresident Alien Income Tax Return for Certain
                              Business Income).
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{}}>
                            <TableCell>
                              <b>Deadline to file:</b>
                              <br />
                              Typically due on <b>March 15th</b> for
                              partnerships and S corporations, and{' '}
                              <b>April 15th</b> for C-Corps, trusts and
                              multi-member LLCs
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <b>Deadline to file:</b>
                              <br />
                              Typically due on <b>April 15th</b>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{
                        mt: 1,

                        textAlign: 'left',
                        borderLeft: '3px solid rgb(102, 60, 0)',
                      }}
                    >
                      💡 Understand the{' '}
                      <Link
                        href="/resources/tax-extension/form-7004-and-4868-comparison/"
                        sx={{ color: '#0202ff' }}
                      >
                        difference between Form 7004 and Form 4868
                      </Link>{' '}
                      in detail with TaxZerone.
                    </Alert>
                  </Box>
                </Box>
              </Box>

              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  6. Does the IRS provide proof of an extension?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mb: 2 }}>
                      The IRS doesn&apos;t typically send a separate
                      confirmation notice as proof of your Form 7004 extension
                      being successfully filed.
                    </Typography>
                    <Typography sx={{ mb: 0 }}>
                      If you e-filed your Form 7004 through an authorized IRS
                      e-file provider, you should receive an immediate
                      confirmation email with a reference number as proof of
                      successful submission. This email serves as your
                      confirmation document.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  borderWidth: 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  '&:hover, &:focus, &:active': { borderWidth: 2 },
                }}
                href="/faqs/form-7004/"
              >
                More FAQs
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form7004;
